/**
 * Pagina di import (SPEC 002).
 *
 * Il flusso è in due tempi di proposito: prima l'**anteprima**, che non scrive
 * nulla, poi la conferma. Un import applicato crea portafogli, movimenti e
 * snapshot; darne prima una lettura verificabile — con l'esito della
 * validazione incrociata posizione per posizione — è ciò che rende accettabile
 * fidarsi dei numeri.
 */

import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatQuantity,
  INSTRUMENT_TYPE_LABELS,
  labelFor,
  signOf,
} from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Spinner, StatCard, TableWrap } from '@/ui';
import { useImports, useRunImport, type ImportResult, type ReportPosition } from './api';
import './imports.css';

const SECTION_LABELS: Record<string, string> = {
  securities: 'Conto titoli',
  private_markets: 'Private Markets',
  crypto: 'Crypto Wallet',
  cash: 'Liquidità',
};

const VALIDATION_LABELS: Record<ReportPosition['crossValidation']['status'], string> = {
  match: 'Coerente',
  mismatch: 'Da verificare',
  only_pdf: 'Solo snapshot',
  only_csv_closed: 'Posizione chiusa',
  only_csv: 'Solo movimenti',
};

function validationTone(
  status: ReportPosition['crossValidation']['status'],
): 'positive' | 'negative' | 'neutral' {
  if (status === 'match') return 'positive';
  if (status === 'mismatch') return 'negative';
  return 'neutral';
}

function toneClass(value: string | null): string {
  const sign = signOf(value);
  return sign === 'positive' ? 'text-positive' : sign === 'negative' ? 'text-negative' : '';
}

function ReportView({ result }: { result: ImportResult }) {
  const { report } = result;
  const hasBothSources = report.inputsUsed.pdf && report.inputsUsed.csv;

  return (
    <div className="imports__report">
      {result.applied ? (
        <Alert variant="info" title="Import applicato">
          Creati {formatNumber(result.created.portfolios)} portafogli,{' '}
          {formatNumber(result.created.instruments)} strumenti,{' '}
          {formatNumber(result.created.transactions)} movimenti e{' '}
          {formatNumber(result.created.holdings)} posizioni.
          {result.created.skippedTransactions > 0 &&
            ` ${formatNumber(result.created.skippedTransactions)} movimenti già presenti sono stati ignorati.`}
        </Alert>
      ) : (
        <Alert variant="info" title="Anteprima">
          Nessun dato è stato salvato. Controlla i valori qui sotto, poi conferma l'import.
        </Alert>
      )}

      {report.validation.needsReview && (
        <Alert
          variant="warning"
          title="Alcuni valori vanno verificati"
          items={report.validation.warnings}
        >
          {report.validation.warnings.length === 0 &&
            'I totali dichiarati dal documento non corrispondono a quelli ricostruiti.'}
        </Alert>
      )}

      {!hasBothSources && (
        <Alert variant="info">
          {report.inputsUsed.pdf
            ? 'Solo lo snapshot: è disponibile il valore attuale, ma non il costo di carico né la ' +
              'cronologia. Carica anche il CSV delle operazioni per ottenerli.'
            : 'Solo la cronologia: sono disponibili costo di carico e movimenti, ma non il valore ' +
              'di mercato attuale. Carica anche il PDF del patrimonio per ottenerlo.'}
        </Alert>
      )}

      {report.validation.positionsWithoutValue.length > 0 && (
        <Alert variant="warning" title="Alcune posizioni sono senza valore di mercato">
          {report.validation.positionsWithoutValue.length} posizioni sono state importate con la
          loro quantità, ma il PDF non ne dichiarava un valore interpretabile. Sono conteggiate
          nel portafoglio e il valore si potrà recuperare dalle quotazioni.{' '}
          <strong>Finché mancano, il valore attuale qui sotto è una sottostima.</strong>
        </Alert>
      )}

      <section className="imports__stats">
        <StatCard
          label="Valore attuale"
          value={formatCurrency(report.totals.currentValueEur)}
          hint={
            report.validation.positionsWithoutValue.length > 0
              ? `Parziale: ${report.validation.positionsWithoutValue.length} posizioni non valorizzate`
              : report.snapshot
                ? `Snapshot del ${formatDate(report.snapshot.date)}`
                : 'Non disponibile'
          }
        />
        <StatCard
          label="Costo di carico"
          value={formatCurrency(report.totals.costBasisEur)}
          hint="Costo medio ponderato"
        />
        <StatCard
          label="P&L non realizzata"
          value={formatCurrency(report.totals.unrealizedPlEur)}
          tone={
            signOf(report.totals.unrealizedPlEur) === 'positive'
              ? 'positive'
              : signOf(report.totals.unrealizedPlEur) === 'negative'
                ? 'negative'
                : 'neutral'
          }
          hint={hasBothSources ? 'Solo posizioni con entrambe le fonti' : 'Richiede entrambi i file'}
        />
        <StatCard
          label="P&L realizzata"
          value={formatCurrency(report.totals.realizedPlEur)}
          tone={
            signOf(report.totals.realizedPlEur) === 'positive'
              ? 'positive'
              : signOf(report.totals.realizedPlEur) === 'negative'
                ? 'negative'
                : 'neutral'
          }
          hint="Da vendite già eseguite"
        />
      </section>

      <Card
        title="Posizioni"
        subtitle={
          hasBothSources
            ? 'La colonna "Coerenza" confronta la quantità dello snapshot con quella ricostruita dai movimenti'
            : 'Validazione incrociata non disponibile con un solo file'
        }
        flush
      >
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Strumento</th>
                <th className="col--tertiary">Sezione</th>
                <th className="numeric col--secondary">Quantità</th>
                <th className="numeric">Valore</th>
                <th className="numeric col--secondary">Costo</th>
                <th className="numeric">P&amp;L</th>
                <th>Coerenza</th>
              </tr>
            </thead>
            <tbody>
              {report.positions.map((position) => (
                <tr key={position.isinOrTicker}>
                  <td>
                    <div className="imports__instrument">
                      <span>{position.name}</span>
                      <span className="imports__instrument-meta">
                        {position.isinOrTicker} ·{' '}
                        {labelFor(INSTRUMENT_TYPE_LABELS, position.assetType)}
                      </span>
                    </div>
                  </td>
                  <td className="col--tertiary">
                    {position.section ? (
                      <Badge>{SECTION_LABELS[position.section] ?? position.section}</Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="numeric col--secondary">
                    {formatQuantity(
                      position.current?.quantity ?? position.history?.netQuantity ?? null,
                    )}
                  </td>
                  <td className="numeric">
                    {position.current?.valueMissing ? (
                      <Badge tone="negative">da valorizzare</Badge>
                    ) : (
                      formatCurrency(position.current?.valueEur ?? null)
                    )}
                  </td>
                  <td className="numeric col--secondary">
                    {formatCurrency(position.history?.costBasisEur ?? null)}
                  </td>
                  <td className={`numeric ${toneClass(position.unrealizedPlEur)}`}>
                    {position.unrealizedPlEur === null ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <>
                        {formatCurrency(position.unrealizedPlEur)}
                        <span className="imports__pct">{formatPercent(position.unrealizedPlPct)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <span title={position.crossValidation.note ?? undefined}>
                      <Badge tone={validationTone(position.crossValidation.status)}>
                        {VALIDATION_LABELS[position.crossValidation.status]}
                      </Badge>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      {report.validation.positionsWithMismatch.length > 0 && (
        <Card title="Posizioni da verificare">
          <ul className="imports__notes">
            {report.positions
              .filter((position) => position.crossValidation.status === 'mismatch')
              .map((position) => (
                <li key={position.isinOrTicker}>
                  <strong>{position.name}</strong>
                  <div className="imports__note-detail">
                    Snapshot {formatQuantity(position.crossValidation.pdfQuantity)} · movimenti{' '}
                    {formatQuantity(position.crossValidation.csvNetQuantity)} · differenza{' '}
                    {formatQuantity(position.crossValidation.delta)}
                  </div>
                  {position.crossValidation.note && (
                    <p className="imports__note-cause">{position.crossValidation.note}</p>
                  )}
                </li>
              ))}
          </ul>
        </Card>
      )}

      {report.history && (
        <Card title="Cronologia" subtitle="Ricostruita dal CSV delle operazioni">
          <dl className="imports__facts">
            <dt>Periodo coperto</dt>
            <dd>
              {report.history.window
                ? `${formatDate(report.history.window.from)} → ${formatDate(report.history.window.to)}`
                : '—'}
            </dd>
            <dt>Movimenti letti</dt>
            <dd className="numeric">{formatNumber(report.history.eventsCount)}</dd>
            <dt>Duplicati scartati</dt>
            <dd className="numeric">{formatNumber(report.history.duplicatesRemoved)}</dd>
            <dt>Versamenti</dt>
            <dd className="numeric">{formatCurrency(report.totals.depositsEur)}</dd>
            <dt>Dividendi</dt>
            <dd className="numeric text-positive">{formatCurrency(report.totals.dividendsEur)}</dd>
            <dt>Interessi</dt>
            <dd className="numeric text-positive">{formatCurrency(report.totals.interestEur)}</dd>
            <dt>Commissioni</dt>
            <dd className="numeric">{formatCurrency(report.totals.feesPaidEur)}</dd>
            <dt>Imposte</dt>
            <dd className="numeric">{formatCurrency(report.totals.taxesPaidEur)}</dd>
          </dl>
        </Card>
      )}
    </div>
  );
}

export function ImportPage() {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const runImport = useRunImport();
  const importsQuery = useImports();

  const hasFile = pdfFile !== null || csvFile !== null;
  // Dopo un'anteprima riuscita si può confermare senza rileggere i file:
  // sono ancora quelli selezionati nel form.
  const canConfirm = hasFile && result !== null && !result.applied;

  const submit = async (dryRun: boolean): Promise<void> => {
    setError(null);
    try {
      const payload = await runImport.mutateAsync({
        pdf: pdfFile ?? undefined,
        csv: csvFile ?? undefined,
        dryRun,
      });
      setResult(payload);
      if (payload.applied) {
        // L'import è andato a buon fine: i file non servono più, e lasciarli
        // selezionati inviterebbe a rimandarli generando un conflitto.
        setPdfFile(null);
        setCsvFile(null);
        if (pdfInputRef.current) pdfInputRef.current.value = '';
        if (csvInputRef.current) csvInputRef.current.value = '';
      }
    } catch (caught) {
      setResult(null);
      setError(
        caught instanceof ApiError ? caught.message : 'Si è verificato un errore imprevisto.',
      );
    }
  };

  const onFileChange =
    (setter: (file: File | null) => void) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      setter(event.target.files?.[0] ?? null);
      setResult(null);
      setError(null);
    };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit(true);
  };

  return (
    <div className="imports">
      <header className="imports__header">
        <div>
          <h1 className="imports__title">Import dati</h1>
          <p className="imports__subtitle">
            Carica l'estratto del patrimonio (PDF) e/o la cronologia operazioni (CSV). I file
            restano sul tuo server e non vengono salvati su disco.
          </p>
        </div>
      </header>

      <Card title="Carica i file" subtitle="Entrambi facoltativi: puoi caricarne anche uno solo">
        <form className="imports__form" onSubmit={onSubmit}>
          <div className="imports__fields">
            <div className="imports__field">
              <label className="imports__label" htmlFor="import-pdf">
                Estratto del patrimonio netto (PDF)
              </label>
              <input
                id="import-pdf"
                ref={pdfInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="imports__file"
                onChange={onFileChange(setPdfFile)}
              />
              <span className="imports__hint">Dà il valore di mercato attuale delle posizioni.</span>
            </div>

            <div className="imports__field">
              <label className="imports__label" htmlFor="import-csv">
                Esportazione operazioni (CSV)
              </label>
              <input
                id="import-csv"
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="imports__file"
                onChange={onFileChange(setCsvFile)}
              />
              <span className="imports__hint">
                Dà costo di carico, movimenti, dividendi e commissioni.
              </span>
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <div className="imports__actions">
            <Button type="submit" variant="secondary" disabled={!hasFile} loading={runImport.isPending}>
              Anteprima
            </Button>
            <Button
              type="button"
              disabled={!canConfirm}
              loading={runImport.isPending}
              onClick={() => void submit(false)}
            >
              Conferma import
            </Button>
          </div>
        </form>
      </Card>

      {result && <ReportView result={result} />}

      <Card title="Import precedenti" flush>
        {importsQuery.isPending ? (
          <Spinner />
        ) : (importsQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon="📥"
            title="Nessun import ancora"
            description="Carica un file qui sopra per iniziare."
          />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>File</th>
                  <th className="col--secondary">Tipo</th>
                  <th>Data</th>
                  <th className="numeric col--secondary">Righe</th>
                  <th>Esito</th>
                </tr>
              </thead>
              <tbody>
                {importsQuery.data?.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.originalFilename}</td>
                    <td className="col--secondary">
                      <Badge tone="accent">{item.sourceKind?.toUpperCase() ?? '—'}</Badge>
                    </td>
                    <td className="cell--minor">{formatDateTime(item.createdAt)}</td>
                    <td className="numeric col--secondary">{formatNumber(item.importedRows)}</td>
                    <td>
                      {item.isDryRun ? (
                        <Badge>Anteprima</Badge>
                      ) : (
                        <Badge tone={item.status === 'processed' ? 'positive' : 'negative'}>
                          {item.status === 'processed' ? 'Completato' : 'Con avvisi'}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
